import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SegmentControl } from "@/components/ui/segment-control";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast, useToastError } from "@/components/ui/use-toast";
import {
  TeamCreditsActionEnum,
  TeamCreditsStackPoolEnum,
  TeamCreditsStackSourceEnum,
} from "@/enums/team-credits";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { AdminTeamDTO, TeamCreditsHistoryDTO } from "@shaarp/common";
import { GiftIcon, HistoryIcon, Loader2Icon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import DataTableTeamCreditsHistory from "../data-table-admin-team-credits/data-table-team-credits-history";

interface DialogManageCreditsProps {
  team: AdminTeamDTO;
  open: boolean;
  onClose: () => void;
}

export function DialogManageCredits({
  team,
  open,
  onClose,
}: DialogManageCreditsProps) {
  const { t } = useTranslation();
  const api = useApi();
  const { toast } = useToast();
  const toastError = useToastError();
  const [creditsAmount, setCreditsAmount] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [creditsHistory, setCreditsHistory] = useState<TeamCreditsHistoryDTO[]>(
    []
  );
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [transactionToRemove, setTransactionToRemove] =
    useState<TeamCreditsHistoryDTO | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [activeTab, setActiveTab] = useState("history");
  const [filterValue, setFilterValue] = useState<string>("all");
  const [selectedPool, setSelectedPool] = useState<string>(
    TeamCreditsStackPoolEnum.MAIN
  );

  const filterOptions = [
    {
      value: "all",
      label: t(
        "admin.dataTableAdminTeams.dialogs.giftCredits.history.filter.all"
      ),
    },
    {
      value: "purchased",
      label: t(
        "admin.dataTableAdminTeams.dialogs.giftCredits.history.filter.purchased"
      ),
    },
    {
      value: "gifted",
      label: t(
        "admin.dataTableAdminTeams.dialogs.giftCredits.history.filter.gifted"
      ),
    },
    {
      value: "used",
      label: t(
        "admin.dataTableAdminTeams.dialogs.giftCredits.history.filter.used"
      ),
    },
  ];

  const poolOptions = [
    {
      value: TeamCreditsStackPoolEnum.MAIN,
      label: t(
        "admin.dataTableAdminTeams.dialogs.giftCredits.poolSelector.main"
      ),
    },
    {
      value: TeamCreditsStackPoolEnum.API_PUBLIC,
      label: t(
        "admin.dataTableAdminTeams.dialogs.giftCredits.poolSelector.api"
      ),
    },
  ];

  const filteredCreditsHistory = useMemo(() => {
    switch (filterValue) {
      case "purchased":
        return creditsHistory.filter(
          (transaction) =>
            transaction.sourceStack === TeamCreditsStackSourceEnum.PURCHASE &&
            transaction.action === TeamCreditsActionEnum.ADD
        );
      case "gifted":
        return creditsHistory.filter(
          (transaction) =>
            transaction.sourceStack === TeamCreditsStackSourceEnum.GIFT &&
            transaction.action === TeamCreditsActionEnum.ADD
        );
      case "used":
        return creditsHistory.filter(
          (transaction) => transaction.action === TeamCreditsActionEnum.USE
        );
      default:
        return creditsHistory;
    }
  }, [creditsHistory, filterValue]);

  const fetchCreditsHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const { data } = await api.get<TeamCreditsHistoryDTO[]>(
        `/admin/team/${team.id}/credits/history`
      );
      setCreditsHistory(data);
    } catch (error) {
      toastError();
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchCreditsHistory();
    }
  }, [open, team.id]);

  const handleGiftCredits = () => {
    const amount = parseInt(creditsAmount);

    if (!amount || amount <= 0) {
      toast({
        title: t("admin.dataTableAdminTeams.dialogs.giftCredits.error.title"),
        description: t(
          "admin.dataTableAdminTeams.dialogs.giftCredits.error.invalidAmount"
        ),
        variant: "destructive",
      });
      return;
    }

    setShowConfirmation(true);
  };

  const handleConfirmGift = async () => {
    const amount = parseInt(creditsAmount);
    setIsLoading(true);
    setShowConfirmation(false);

    try {
      await api.post(`/admin/team/${team.id}/gift-credits`, {
        amount,
        pool: selectedPool === TeamCreditsStackPoolEnum.API_PUBLIC
          ? "API_PUBLIC"
          : undefined,
      });

      toast({
        title: t("admin.dataTableAdminTeams.dialogs.giftCredits.success.title"),
        description: t(
          "admin.dataTableAdminTeams.dialogs.giftCredits.success.description",
          {
            amount,
            teamName: team.name,
          }
        ),
      });

      await fetchCreditsHistory();
      setActiveTab("history");
      setCreditsAmount("");
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelGift = () => {
    setShowConfirmation(false);
  };

  const handleRemoveTransaction = (transaction: TeamCreditsHistoryDTO) => {
    setTransactionToRemove(transaction);
    setShowRemoveConfirmation(true);
  };

  const handleConfirmRemove = async () => {
    if (!transactionToRemove) return;

    setIsRemoving(true);
    setShowRemoveConfirmation(false);

    try {
      await api.delete(
        `/admin/team/${team.id}/credits/transaction/${transactionToRemove.id}`
      );

      toast({
        title: t(
          "admin.dataTableAdminTeams.dialogs.giftCredits.remove.success.title"
        ),
        description: t(
          "admin.dataTableAdminTeams.dialogs.giftCredits.remove.success.description",
          {
            amount: transactionToRemove.amount,
            teamName: team.name,
          }
        ),
      });

      handleClose();
    } catch (error) {
      toastError();
    } finally {
      setIsRemoving(false);
      setTransactionToRemove(null);
    }
  };

  const handleCancelRemove = () => {
    setShowRemoveConfirmation(false);
    setTransactionToRemove(null);
  };

  const handleClose = () => {
    setCreditsAmount("");
    setShowConfirmation(false);
    setShowRemoveConfirmation(false);
    setTransactionToRemove(null);
    setActiveTab("history");
    setFilterValue("all");
    setSelectedPool(TeamCreditsStackPoolEnum.MAIN);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          <div className="flex items-center space-x-2">
            <DialogTitle className="flex justify-between items-center w-full">
              <span className="text-left">
                {t("admin.dataTableAdminTeams.dialogs.giftCredits.title", {
                  teamName: team.name,
                })}
              </span>
              <span className="flex gap-3 items-center text-right">
                <span>
                  <span className="text-muted-foreground">
                    {t(
                      "admin.dataTableAdminTeams.dialogs.giftCredits.poolSelector.main"
                    )}:
                  </span>{" "}
                  {team.creditsBalance}
                </span>
                <span>
                  <span className="text-muted-foreground">
                    {t(
                      "admin.dataTableAdminTeams.dialogs.giftCredits.poolSelector.api"
                    )}:
                  </span>{" "}
                  {team.apiCreditsBalance}
                </span>
              </span>
            </DialogTitle>
          </div>
        </DialogHeader>

        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full min-w-0"
        >
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger
              value="history"
              className="flex items-center space-x-2"
            >
              <HistoryIcon className="w-4 h-4" />
              <span>
                {t(
                  "admin.dataTableAdminTeams.dialogs.giftCredits.tabs.history"
                )}
              </span>
            </TabsTrigger>
            <TabsTrigger value="gift" className="flex items-center space-x-2">
              <GiftIcon className="w-4 h-4" />
              <span>
                {t("admin.dataTableAdminTeams.dialogs.giftCredits.tabs.gift")}
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="mt-4">
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-4">
                  <span>
                    {t("admin.dataTableAdminTeams.dialogs.history.title")}
                  </span>
                  <SegmentControl
                    value={filterValue}
                    onValueChange={setFilterValue}
                    options={filterOptions}
                    size="sm"
                  />
                </div>
                <div className="min-w-0 overflow-x-auto">
                  <DataTableTeamCreditsHistory
                    creditsHistory={filteredCreditsHistory}
                    isLoading={isLoadingHistory}
                    onRemove={handleRemoveTransaction}
                  />
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="gift" className="mt-4">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>
                  {t(
                    "admin.dataTableAdminTeams.dialogs.giftCredits.fields.pool.label"
                  )}
                </Label>
                <SegmentControl
                  value={selectedPool}
                  onValueChange={setSelectedPool}
                  options={poolOptions}
                  size="sm"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="credits-amount">
                  {t(
                    "admin.dataTableAdminTeams.dialogs.giftCredits.fields.amount.label"
                  )}
                </Label>
                <Input
                  id="credits-amount"
                  type="number"
                  min="1"
                  placeholder={t(
                    "admin.dataTableAdminTeams.dialogs.giftCredits.fields.amount.placeholder"
                  )}
                  value={creditsAmount}
                  onChange={(e) => setCreditsAmount(e.target.value)}
                  disabled={isLoading}
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="flex-1"
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  onClick={handleGiftCredits}
                  disabled={
                    isLoading || !creditsAmount || parseInt(creditsAmount) <= 0
                  }
                  className="flex-1"
                >
                  {isLoading ? (
                    <Loader2Icon className="mr-2 w-4 h-4 animate-spin" />
                  ) : (
                    t(
                      "admin.dataTableAdminTeams.dialogs.giftCredits.actions.gift"
                    )
                  )}
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>

      <Dialog open={showConfirmation} onOpenChange={handleCancelGift}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center space-x-2">
              <DialogTitle>
                {t(
                  "admin.dataTableAdminTeams.dialogs.giftCredits.confirmation.title"
                )}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              {t(
                "admin.dataTableAdminTeams.dialogs.giftCredits.confirmation.description",
                {
                  amount: creditsAmount,
                  teamName: team.name,
                  pool: selectedPool === TeamCreditsStackPoolEnum.API_PUBLIC
                    ? t("admin.dataTableAdminTeams.dialogs.giftCredits.poolSelector.api")
                    : t("admin.dataTableAdminTeams.dialogs.giftCredits.poolSelector.main"),
                }
              )}
            </div>
            <div className="text-sm text-gray-600">
              {t(
                "admin.dataTableAdminTeams.dialogs.giftCredits.confirmation.balance",
                {
                  currentCredits:
                    selectedPool === TeamCreditsStackPoolEnum.API_PUBLIC
                      ? team.apiCreditsBalance
                      : team.creditsBalance,
                }
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelGift}
                disabled={isLoading}
                className="flex-1"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleConfirmGift}
                disabled={isLoading}
                className="flex-1"
              >
                {isLoading ? (
                  <Loader2Icon className="mr-2 w-4 h-4 animate-spin" />
                ) : (
                  t(
                    "admin.dataTableAdminTeams.dialogs.giftCredits.confirmation.confirm"
                  )
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showRemoveConfirmation} onOpenChange={handleCancelRemove}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center space-x-2">
              <DialogTitle>
                {t(
                  "admin.dataTableAdminTeams.dialogs.giftCredits.remove.confirmation.title"
                )}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              {t(
                "admin.dataTableAdminTeams.dialogs.giftCredits.remove.confirmation.description",
                {
                  amount: transactionToRemove?.amount,
                  teamName: team.name,
                }
              )}
            </div>
            <div className="text-sm text-gray-600">
              {t(
                "admin.dataTableAdminTeams.dialogs.giftCredits.remove.confirmation.balance",
                {
                  currentCredits: team.creditsBalance,
                }
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelRemove}
                disabled={isRemoving}
                className="flex-1"
              >
                {t("common.cancel")}
              </Button>
              <Button
                onClick={handleConfirmRemove}
                disabled={isRemoving}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {isRemoving ? (
                  <Loader2Icon className="mr-2 w-4 h-4 animate-spin" />
                ) : (
                  t(
                    "admin.dataTableAdminTeams.dialogs.giftCredits.remove.confirmation.confirm"
                  )
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
